import { ElementsSDK } from '@yiluhub/ui-sdk-react';
import { getYiluConfig } from '@yiluhub/ui-utilities';
import { ProductConsumerAttributeDto } from '@yiluhub/yilu-amp-types';
import React, { FC } from 'react';
import { Control, Controller, FieldValues, SetFieldValue, UseFormTrigger } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { yiluEnv } from 'utils/index';

import Header from 'components/TravellerInformationForm/components/Header';
import { useSalutations } from 'components/TravellerInformationForm/utils/salutations';

import { ReservationInformationFormContentProps } from '../components/types';
import {
  PhoneInputType,
  ProductConsumerAttributeType,
  getDefaultCountryValue,
  getPhoneNumberFieldValues,
  getSortedCountriesAlpha2Options,
  getValidationRules,
} from '../utils';
import './styles.scss';
import { getEurailFieldsToRender } from './utils/eurailUtils';

export type EurailRenderFieldsProps = {
  control: Control;
  trigger: UseFormTrigger<FieldValues>;
  setValue: SetFieldValue<any>;
  fieldsToRender: ProductConsumerAttributeDto[];
};

const EurailFields: FC<EurailRenderFieldsProps> = ({
  fieldsToRender,
  control,
  trigger,
  setValue,
}) => {
  const { t } = useTranslation();
  const { locale } = getYiluConfig();
  const envVariables = yiluEnv.getVariables();

  const getAndSetSessionStorageValueInitially = (key: string): string => {
    const savedItem = sessionStorage.getItem(key);
    const parsed = savedItem ? JSON.parse(savedItem) : undefined;
    if (parsed && parsed !== '') {
      setValue(key, parsed);
    }
    return parsed;
  };

  const salutationOptions: ElementsSDK.SelectOption[] = [
    { value: 'mr', label: t('tip.reservation.form.field.salutation.option.mr') },
    { value: 'mrs', label: t('tip.reservation.form.field.salutation.option.mrs') },
  ];

  const getFieldLabel = (fieldName: string) => {
    // Match pattern: e.g. "youths_firstName_1"
    const regex = /^[a-zA-Z]+_(.+)_\d+$/;
    const match = regex.exec(fieldName);
    const key = match ? match[1] : fieldName;

    return t(key, { defaultValue: key });
  };

  const { salutationSelectOptions, getDefaultSalutationValue } = useSalutations(salutationOptions);

  return (
    <>
      {fieldsToRender.map((field) => {
        if (field.type === ProductConsumerAttributeType.PHONE_NUMBER) {
          return (
            <Controller
              key={field.name}
              name={field.name.replace('.', '_')}
              control={control}
              rules={getValidationRules(field)}
              render={({
                field: { onChange, onBlur, name, ref, value },
                fieldState: { isTouched, error },
              }) => (
                <ElementsSDK.InputField.PhoneInputField
                  dataTestId="customer-phone"
                  codeValue={value?.code || getPhoneNumberFieldValues(PhoneInputType.CODE, name)}
                  phoneValue={
                    value?.number || getPhoneNumberFieldValues(PhoneInputType.NUMBER, name)
                  }
                  label={t('customer.mobile')}
                  className="yilu-EurailReservationForm__field"
                  onChange={(val) => {
                    sessionStorage.setItem(name, JSON.stringify(val));
                    setValue(name, val);
                    onChange(val);
                    isTouched && trigger(name);
                  }}
                  onBlur={() => {
                    onBlur();
                    trigger(name);
                  }}
                  phoneRef={ref}
                  errorMessage={error?.message && t(error.message)}
                  defaultCountry={envVariables?.PHONE_COUNTRY_CODE ?? 'DE'}
                />
              )}
            />
          );
        }

        if (field.type === ProductConsumerAttributeType.EMAIL) {
          return (
            <Controller
              key={field.name}
              name={field.name.replace('.', '_')}
              control={control}
              rules={getValidationRules(field)}
              render={({
                field: { onChange, onBlur, name, ref, value },
                fieldState: { isTouched, error },
              }) => {
                return (
                  <ElementsSDK.InputField.Select
                    isEmailDropdown
                    showDropdownIcon={false}
                    dataTestId="eurail-reservation-email"
                    label={t('customer.email')}
                    value={value || getAndSetSessionStorageValueInitially(name)}
                    name={name}
                    className="yilu-EurailReservationForm__field"
                    onInputChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(e);
                      isTouched && trigger(name);
                      setValue(e.target.value);
                      sessionStorage.setItem(name, JSON.stringify(e.target.value));
                    }}
                    onSelect={(selectedValue: unknown) => {
                      onChange(selectedValue);
                      isTouched && trigger(name);
                      setValue(selectedValue);
                      sessionStorage.setItem(name, JSON.stringify(selectedValue));
                    }}
                    onBlur={() => {
                      onBlur();
                      trigger(name);
                    }}
                    inputRef={ref}
                    errorMessage={error?.message && t(error.message)}
                  />
                );
              }}
            />
          );
        }

        if (field.name.includes('title')) {
          return (
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              rules={getValidationRules(field)}
              render={({ field: { onChange, name }, fieldState: { error } }) => (
                <ElementsSDK.InputField.Select
                  dataTestId={`${name}-title`}
                  options={salutationSelectOptions}
                  defaultOption={getDefaultSalutationValue(name)}
                  label={t('tip.salutation.label')}
                  placeholder={t('tip.reservation.form.field.salutation.placeholder')}
                  className="yilu-EurailReservationForm__field"
                  onSelect={(val: unknown) => {
                    sessionStorage.setItem(name, JSON.stringify(val));
                    setValue(name, val);
                    onChange(val);
                  }}
                  disabledTyping={true}
                  errorMessage={error?.message && t(error.message)}
                />
              )}
            />
          );
        }

        if (field.name.includes('nationality')) {
          return (
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              rules={getValidationRules(field)}
              render={({ field: { onChange, name }, fieldState: { error } }) => {
                return (
                  <ElementsSDK.InputField.Select
                    dataTestId="country-select"
                    defaultOption={getDefaultCountryValue(name, locale as 'en' | 'de', 'alpha2')}
                    options={getSortedCountriesAlpha2Options(locale as 'en' | 'de')}
                    label={t('nationality')}
                    placeholder={t('tip.parking.reservation.form.field.country.placeholder')}
                    className="yilu-EurailReservationForm__field"
                    onSelect={(selectedValue: unknown) => {
                      onChange(selectedValue);
                      sessionStorage.setItem(name, JSON.stringify(selectedValue));
                      setValue(selectedValue);
                    }}
                    errorMessage={error?.message && t(error.message)}
                  />
                );
              }}
            />
          );
        }

        return (
          <Controller
            key={field.name}
            name={field.name}
            control={control}
            rules={getValidationRules(field)}
            render={({ field: { onChange, onBlur, name, ref, value }, fieldState: { error } }) => (
              <ElementsSDK.InputField.TextInput
                label={getFieldLabel(field.name)}
                value={value || getAndSetSessionStorageValueInitially(name)}
                name={name}
                className="yilu-EurailReservationForm__field"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                  setValue(name, e.target.value);
                  sessionStorage.setItem(name, JSON.stringify(e.target.value));
                }}
                onBlur={() => {
                  onBlur();
                  trigger(name);
                }}
                inputRef={ref}
                errorMessage={error?.message && t(error.message)}
              />
            )}
          />
        );
      })}
    </>
  );
};

const EurailReservationInformationForm: React.FC<ReservationInformationFormContentProps> = ({
  fields,
  control,
  trigger,
  setValue,
}) => {
  const { t } = useTranslation();
  const fieldsToRender = getEurailFieldsToRender(fields);

  // Group by traveller index
  const travellers: Record<string, ProductConsumerAttributeDto[]> = {};

  fieldsToRender.forEach((field) => {
    const regex = /^(adults|youths|seniors|children)_(\w+?)_(\d+)$/;
    const match = regex.exec(field.name);
    if (!match) return;

    const [, type, , index] = match;
    const travellerKey = `${type}_${index}`;

    if (!travellers[travellerKey]) travellers[travellerKey] = [];
    travellers[travellerKey].push(field);
  });

  // Build traveller label
  const getTravellerLabel = (travellerKey: string, order: number) => {
    const [type] = travellerKey.split('_');
    const capitalisedType = type.charAt(0).toUpperCase() + type.slice(1, -1); // Adult, Youth, Senior, Child

    return t('tip.eurail.reservation.form.travellerSectionTitle', {
      number: order,
      type: capitalisedType,
      defaultValue: `Traveller ${order} (${capitalisedType})`,
    });
  };

  return (
    <div className="yilu-EurailReservationForm">
      <ElementsSDK.Message
        type={ElementsSDK.MessageType.INFORMATIVE}
        content={t('eurail.tip.alert')}
        className="eurail-tip-message"
      />
      {Object.entries(travellers).map(([travellerKey, travellerFields], order) => (
        <div key={travellerKey} className="yilu-EurailReservationForm__travellerSection">
          <Header
            heading={getTravellerLabel(travellerKey, order + 1)}
            className="yilu-EurailReservationForm__header"
          />

          <div className="yilu-EurailReservationForm__travellerFields">
            <EurailFields
              control={control}
              trigger={trigger}
              setValue={setValue}
              fieldsToRender={travellerFields}
            />
          </div>

          {order < Object.keys(travellers).length - 1 && (
            <hr className="yilu-EurailReservationForm__divider" />
          )}
        </div>
      ))}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default EurailReservationInformationForm;
